import { SortingState } from "@tanstack/react-table";
import EditableDateCell from "Common/EditableDateCell";
import EditableTextCell from "Common/EditableTextCell";
import TableContainer from "Common/TableContainer";
import LoaderErrorWrapper from "Components/LoaderErrorWrapper/LoaderErrorWrapper";
import { QuotationActionMenu } from "Components/MenuActionButton/QuotationMenu";
import { StatusCell } from "Components/Other/StatusCell";
import { FollowUpTypeCell } from "Components/Other/FollowUpTypeCell";
import { app_modules } from "helpers/apps_modules_list";
import {
  useHandleDownloadPDF,
  useHandleDownloadPIInvoice1,
} from "helpers/functions/handleDownloadPDF";
import {
  useHandleDownloadPDF2,
  useHandleDownloadPIInvoice2,
} from "helpers/functions/handleDownloadPDF2";
import { formatDate } from "helpers/functions/formatDate";
import { formatLabel } from "helpers/functions/formatLabel";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import {
  changeQuotationStatusById,
  deleteQuotationById,
  fetchQuotationLog,
} from "slices/Quotation/thunk";
import { fetchLatestPermissions } from "slices/auth/login/thunk";
import Swal from "sweetalert2";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Spinner,
  Accordion,
  Table,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { Autocomplete, TextField } from "@mui/material";
import { autocompleteDropdownStyles } from "Constants/Style";
import { Axios } from "api/axios";

type QuotationLogProps = {
  handleTabChange?: (tabIndex: number) => void;
  searchInput: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  search: string;
  colValue?: any;
  setColValue?: React.Dispatch<React.SetStateAction<any>>;
  resetTrigger?: number;
  onDashboardRefresh?: () => void;
};

const QuotationLog: React.FC<QuotationLogProps> = ({
  handleTabChange,
  searchInput,
  setSearch,
  search,
  colValue,
  setColValue,
  resetTrigger,
  onDashboardRefresh,
}) => {
  document.title = "Quotation";
  const dispatch = useDispatch<any>();

  // Use PDF download hooks
  const handleDownloadPDF = useHandleDownloadPDF();
  const handleDownloadPDF2 = useHandleDownloadPDF2();
  const handlePIInvoice1 = useHandleDownloadPIInvoice1();
  const handlePIInvoice2 = useHandleDownloadPIInvoice2();

  // Function to refresh quotation data after PI generation
  const refreshQuotationData = useCallback(() => {
    dispatch(
      fetchQuotationLog({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: colValue,
      })
    );
    // Also call dashboard refresh if available
    if (onDashboardRefresh) {
      onDashboardRefresh();
    }
  }, [dispatch, search, colValue, onDashboardRefresh]);

  const selectQuotation = createSelector(
    (state: any) => state.QuotationReducer || [],
    (quotation) => ({
      quotationList: quotation.list,
      rowCount: quotation.rowCount,
      loading: quotation.loading,
      error: quotation.error,
    })
  );

  const user = useSelector((state: any) => state.Login.user);
  const user_permissions = user?.permissions || [];
  // console.log(user_permissions,"user_permission------11")
  const master_permissions = user_permissions?.find(
    (item: any) => item?.apps_name === app_modules?.quotation?.app_name
  );
  const quotation_permissions = master_permissions?.modules_permissions?.find(
    (item: any) =>
      item?.modules_name === app_modules?.quotation?.modules?.quotation
  );
  // console.log(quotation_permissions, "quotation_permissions--------11");
  const { quotationList, rowCount, loading, error } =
    useSelector(selectQuotation);

  //*// UserType with refreashed user(in permission fetch)
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const isAdmin = authUser?.user_type === "Admin";

  //*// For redirecting back to this page and get the same paginated pageIndex
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get("page_quotation") || "0");

  const [pageIndex, setPageIndex] = useState(pageParam);

  // Save current page to sessionStorage when component mounts
  useEffect(() => {
    if (pageParam > 0) {
      sessionStorage.setItem("quotationPage", pageParam.toString());
    } else {
      // Clear sessionStorage when navigating from sidebar (page 0)
      sessionStorage.removeItem("quotationPage");
    }
  }, [pageParam]);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<any>();

  // const [filterValue, setFilterValue] = useState<any[]>([]);

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  useEffect(() => {
    dispatch(
      fetchQuotationLog({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: colValue,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search,
    // colValue,
    sortField,
    sortDirection,
  ]);

  const prevFilters = useRef(columnFilters);
  const prevColValue = useRef(colValue);

  // Sync columnFilters with colValue to show filters in UI
  const prevColValueForSync = useRef<any>(null);
  useEffect(() => {
    // Only sync if colValue actually changed
    if (
      JSON.stringify(prevColValueForSync.current) === JSON.stringify(colValue)
    ) {
      return;
    }
    prevColValueForSync.current = colValue;

    if (!colValue || !Array.isArray(colValue) || colValue.length === 0) {
      // If colValue is empty, clear all column filters
      setColumnFilters([]);
      return;
    }

    // Convert colValue to columnFilters format
    const newColumnFilters = colValue.map((filter: any) => ({
      id: filter.filterField,
      value: filter.filterValue,
    }));

    setColumnFilters(newColumnFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colValue]);

  useEffect(() => {
    // Clear data immediately when filters change to prevent showing old data
    const colValueChanged =
      JSON.stringify(prevColValue.current) !== JSON.stringify(colValue);
    const filtersChanged =
      JSON.stringify(prevFilters.current) !== JSON.stringify(columnFilters);

    if (colValueChanged) {
      setData([]);
      prevColValue.current = colValue;
    }

    if (colValueChanged || filtersChanged) {
      if (filtersChanged) {
        prevFilters.current = columnFilters;
      }
      dispatch(
        fetchQuotationLog({
          page: 1, // Always use page 1 when filters change
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: colValue,
        })
      );
    }
  }, [
    colValue,
    columnFilters,
    dispatch,
    pageSize,
    sortField,
    sortDirection,
    search,
  ]);

  useEffect(() => {
    setData(quotationList);
  }, [quotationList]);

  // Sync pageIndex with URL parameters
  useEffect(() => {
    const currentPageParam = parseInt(
      searchParams.get("page_quotation") || "0"
    );
    if (currentPageParam !== pageIndex) {
      setPageIndex(currentPageParam);
    }
  }, [searchParams, pageIndex]);

  //*// All pagination Issues(resolved) when redirecting from the edit or view page
  // Reset pagenumber to 0 when not first render
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    handlePageChange(0);
  }, [sorting, pageSize, columnFilters, search]);

  // Reset pagination and sorting when resetTrigger changes
  useEffect(() => {
    if (resetTrigger !== undefined && resetTrigger > 0) {
      // Clear data immediately to prevent showing old data
      setData([]);
      setSorting([]);
      setPageIndex(0);
      setSearchParams({ page_quotation: "0" });
      sessionStorage.removeItem("quotationPage");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetTrigger]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPageIndex(newPage);
      setSearchParams({ page_quotation: newPage.toString() });
      // Save current page to sessionStorage for back navigation
      sessionStorage.setItem("quotationPage", newPage.toString());
    },
    [setSearchParams]
  );
  // Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);
  //*//

  //*// Fetch Permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        await dispatch(fetchLatestPermissions()).unwrap();
      } catch (error) {
        console.error("Failed to fetch latest permissions", error);
        // toast.error("Failed to fetch latest permissions");
      }
    };
    fetchPermissions();
  }, [dispatch]);

  const handleDelete = useCallback(
    async (id: any, rowData?: any) => {
      // Check if user is admin
      if (!isAdmin) {
        toast.error("Only administrators can delete quotations");
        return;
      }

      // Check if quotation status is PI Generated
      if (rowData?.current_status === "PI Generated") {
        toast.error("PI Generated quotations cannot be deleted");
        return;
      }
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "Are You Sure You Want To Delete Quotation!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
        width: "350px",
        customClass: {
          popup: "swal2-sm-popup",
        },
      });

      if (!result.isConfirmed) return;

      await dispatch(deleteQuotationById(id))
        .unwrap()
        .then(() => {
          toast.success(`Quotation deleted successfully!!`);

          if (data.length === 1 && pageIndex > 0) {
            handlePageChange(pageIndex - 1);
          } else {
            dispatch(
              fetchQuotationLog({
                page: pageIndex + 1,
                pageSize,
                sort: sortField,
                order: sortField ? sortDirection : "",
                search,
                filters: colValue,
              })
            );
          }

          // Refresh dashboard data after deleting quotation
          if (onDashboardRefresh) {
            onDashboardRefresh();
          }
        })
        .catch((err: any) => {
          toast.error("Failed to Delete Quotation");
        });
    },
    [
      dispatch,
      data,
      pageIndex,
      pageSize,
      sortField,
      sortDirection,
      search,
      colValue,
      isAdmin,
      handlePageChange,
      onDashboardRefresh,
    ]
  );

  const fetchQuotationsAfterUpdate = useCallback(async () => {
    await dispatch(
      fetchQuotationLog({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: colValue,
      })
    );
    // Refresh dashboard data after updating quotations
    if (onDashboardRefresh) {
      onDashboardRefresh();
    }
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    colValue,
    onDashboardRefresh,
  ]);

  const handleStatusChange = useCallback(
    async (id: any, newStatus: any) => {
      await dispatch(changeQuotationStatusById({ id: id, payload: newStatus }))
        .unwrap()
        .then(() => {
          toast.success(`Status Changed Successfully!!`);
          fetchQuotationsAfterUpdate();
        })
        .catch((err: any) => {
          console.log(err);
          toast.error("Failed to Change Quotation Status");
        });
    },
    [dispatch, fetchQuotationsAfterUpdate]
  );

  const handleFollowUpTypeChange = useCallback(
    async (id: any, newFollowUpType: any) => {
      // Use the same API endpoint but with follow_up_type in payload
      await dispatch(
        changeQuotationStatusById({
          id: id,
          payload: { follow_up_type: newFollowUpType },
        })
      )
        .unwrap()
        .then(() => {
          toast.success(`Follow Up Type Changed Successfully!!`);
          fetchQuotationsAfterUpdate();
        })
        .catch((err: any) => {
          console.log(err);
          toast.error("Failed to Change Follow Up Type");
        });
    },
    [dispatch, fetchQuotationsAfterUpdate]
  );

  // Follow Up Details Modal State
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [selectedQuotationId, setSelectedQuotationId] = useState<string | null>(
    null
  );
  const [followUpHistory, setFollowUpHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [updatingFollowUp, setUpdatingFollowUp] = useState(false);
  const [accordionOpen, setAccordionOpen] = useState<string | null>(null);
  const [followUpFormData, setFollowUpFormData] = useState({
    follow_up_date: "",
    follow_up_type: "",
    follow_up_remark: "",
    next_follow_up_date: "",
  });

  // Fetch follow-up history
  const fetchFollowUpHistory = useCallback(async (quotationId: string) => {
    setLoadingHistory(true);
    try {
      const response = await Axios.get(
        `api/quotation/${quotationId}/followup-history/`
      );
      // Response structure: { status, message, data: [...] }
      setFollowUpHistory(response.data?.data || []);
    } catch (error: any) {
      console.error("Failed to fetch follow-up history:", error);
      toast.error("Failed to fetch follow-up history");
      setFollowUpHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  // Store current row data for display
  const [currentRowData, setCurrentRowData] = useState<any>(null);

  // Handle opening follow-up modal
  const handleOpenFollowUpModal = useCallback(
    (quotationId: string, rowData: any) => {
      setSelectedQuotationId(quotationId);
      setCurrentRowData(rowData);
      // Reset form to empty instead of autofilling
      setFollowUpFormData({
        follow_up_date: "",
        follow_up_type: "",
        follow_up_remark: "",
        next_follow_up_date: "",
      });
      setShowFollowUpModal(true);
      fetchFollowUpHistory(quotationId);
    },
    [fetchFollowUpHistory]
  );

  // Handle closing follow-up modal
  const handleCloseFollowUpModal = useCallback(() => {
    setShowFollowUpModal(false);
    setSelectedQuotationId(null);
    setCurrentRowData(null);
    setFollowUpHistory([]);
    setFollowUpFormData({
      follow_up_date: "",
      follow_up_type: "",
      follow_up_remark: "",
      next_follow_up_date: "",
    });
    setAccordionOpen(null);
    fetchQuotationsAfterUpdate();
  }, []);

  // Handle updating follow-up details
  const handleUpdateFollowUp = useCallback(async () => {
    if (!selectedQuotationId) return;

    // Validate all fields are filled
    if (
      !followUpFormData.follow_up_type ||
      !followUpFormData.follow_up_date ||
      !followUpFormData.follow_up_remark ||
      !followUpFormData.next_follow_up_date
    ) {
      toast.error("Please fill all mandatory fields");
      return;
    }

    setUpdatingFollowUp(true);
    try {
      await dispatch(
        changeQuotationStatusById({
          id: selectedQuotationId,
          payload: followUpFormData,
        })
      )
        .unwrap()
        .then(() => {
          toast.success("Follow-up details updated successfully!");
          fetchFollowUpHistory(selectedQuotationId);
          // Clear the form after successful update
          setFollowUpFormData({
            follow_up_date: "",
            follow_up_type: "",
            follow_up_remark: "",
            next_follow_up_date: "",
          });
          // Close the accordion after successful update
          setAccordionOpen(null);
        })
        .catch((err: any) => {
          console.error("Failed to update follow-up:", err);
          toast.error(
            err?.response?.data?.message || "Failed to update follow-up details"
          );
        });
    } catch (error: any) {
      console.error("Failed to update follow-up:", error);
      toast.error("Failed to update follow-up details");
    } finally {
      setUpdatingFollowUp(false);
    }
  }, [selectedQuotationId, followUpFormData, dispatch, fetchFollowUpHistory]);

  // Custom Scrollable Tooltip Component
  const ScrollableTooltip = ({
    id,
    content,
    children,
    placement = "auto",
  }: {
    id: string;
    content: string;
    children: React.ReactElement;
    placement?: string;
  }) => {
    const [show, setShow] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleMouseEnter = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setShow(true);
    };

    const handleMouseLeave = () => {
      timeoutRef.current = setTimeout(() => {
        setShow(false);
      }, 200);
    };

    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    return (
      <OverlayTrigger
        show={show}
        placement={placement as any}
        flip={true}
        popperConfig={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 4],
              },
            },
            {
              name: "preventOverflow",
              options: {
                boundary: "viewport",
                padding: 8,
              },
            },
            {
              name: "flip",
              options: {
                fallbackPlacements: ["top", "bottom", "left", "right"],
              },
            },
          ],
        }}
        overlay={
          <Tooltip
            id={id}
            className="light-tooltip"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div
              style={{
                whiteSpace: "pre-wrap",
                maxWidth: "400px",
                maxHeight: "300px",
                overflowY: "auto",
                wordBreak: "break-word",
              }}
            >
              {content}
            </div>
          </Tooltip>
        }
      >
        <div
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{ display: "inline-block", width: "100%" }}
        >
          {children}
        </div>
      </OverlayTrigger>
    );
  };

  const DynamicAction = useMemo(
    () => [
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        size: 80,
        cell: ({ row }: any) => (
          <div onClick={(e) => e.stopPropagation()}>
            <QuotationActionMenu
              row={row}
              handleDelete={handleDelete}
              handleDownloadPDF={handleDownloadPDF}
              handleDownloadPDF2={handleDownloadPDF2}
              handlePIInvoice1={(id) =>
                handlePIInvoice1(id, refreshQuotationData)
              }
              handlePIInvoice2={(id) =>
                handlePIInvoice2(id, refreshQuotationData)
              }
              canChange={
                !!quotation_permissions?.permissions?.Update &&
                row.original.current_status !== "PI Generated"
              }
              canDelete={
                isAdmin && row.original.current_status !== "PI Generated"
              }
              handleFollowUpDetails={(quotationId: string, rowData: any) => {
                handleOpenFollowUpModal(quotationId, rowData);
              }}
            />
          </div>
        ),
      },
    ],
    [
      quotation_permissions,
      handleDelete,
      handleDownloadPDF,
      handleDownloadPDF2,
      handlePIInvoice1,
      handlePIInvoice2,
      isAdmin,
      handleOpenFollowUpModal,
      refreshQuotationData,
    ]
  );

  //* Columns (no unnecessary dependencies)
  const columns = useMemo(
    () => [
      // {
      //   header: "Sr. No.",
      //   id: "sr_no", // important: since there's no accessorKey
      //   enableSorting: false,
      //   enableColumnFilter: false,
      //   cell: ({ row }: { row: any }) => {
      //     return row.index + 1 + pageIndex * pageSize;
      //   },
      // },
      {
        header: "Quotation Number",
        accessorKey: "quotation_auto_id",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/api/quotation/column-filter-dropdown/?column_filter=quotation_auto_id",
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal", // allow line breaks
              wordWrap: "break-word", // break long words
              maxWidth: "200px", // set a maximum width
              overflow: "hidden", // prevent overflow
              textOverflow: "ellipsis", // optional: show "..." if truncated
            }}
          >
            {row.original.quotation_auto_id || "-"}
          </div>
        ),
      },
      {
        header: "Customer/Company Name",
        accessorKey: "customer_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/api/quotation/column-filter-dropdown/?column_filter=customer_name",
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal", // allow line breaks
              wordWrap: "break-word", // break long words
              maxWidth: "200px", // set a maximum width
              overflow: "hidden", // prevent overflow
              textOverflow: "ellipsis", // optional: show "..." if truncated
            }}
          >
            {row.original.customer_details.customer_name || "-"}
          </div>
        ),
      },
      {
        header: "Mobile Number",
        accessorKey: "mobile_no",
        // accessorFn: (row: any) => row.customer_details?.mobile_no || "-",
        enableColumnFilter: false,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "api/customers/column-filter-dropdown/?column_filter=mobile_no",
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal", // allow line breaks
              wordWrap: "break-word", // break long words
              maxWidth: "200px", // set a maximum width
              overflow: "hidden", // prevent overflow
              textOverflow: "ellipsis", // optional: show "..." if truncated
            }}
          >
            {row.original.customer_details?.mobile_no || "-"}
          </div>
        ),
      },

      {
        header: "Quotation Date",
        accessorKey: "quotation_date",
        enableColumnFilter: true,
        filterType: "date" as const,
        enableSorting: true,
        cell: ({ row }: { row: any }) => {
          const date = formatDate(row.original.quotation_date);
          return `${date}`;
        },
      },
      {
        header: "Validity Date",
        accessorKey: "validity",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <EditableDateCell
            value={row.original.validity}
            rowId={row.original.quotation_id}
            formatDisplay={formatDate}
            showExpiredBadge={true}
            canChange={
              quotation_permissions?.permissions?.Update &&
              row.original.current_status !== "PI Generated"
            }
            onSuccess={fetchQuotationsAfterUpdate}
          />
        ),
        meta: {
          rawValue: (row: any) => row.validity,
        },
      },
      {
        header: "Status",
        accessorKey: "current_status",
        enableSorting: false,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/api/quotation/column-filter-dropdown/?column_filter=current_status",
        cell: ({ row }: { row: any }) => (
          <StatusCell
            row={row}
            handleStatusChange={handleStatusChange}
            // canChangeStatus={row.original.current_status !== "PI Generated"}
            canChangeStatus={true}
          />
          // <div
          //   style={{ width: "100%", minWidth: "120px" }}
          //   className="d-flex justify-content-start"
          // >
          //   {" "}
          //   {row.original.current_status === "PI Generated" ? (
          //     <span className="badge bg-success text-white">
          //       {row.original.current_status}
          //     </span>
          //   ) : (
          //     <StatusCell
          //       row={row}
          //       handleStatusChange={handleStatusChange}
          //       canChangeStatus={row.original.current_status !== "PI Generated"}
          //     />
          //   )}
          // </div>
        ),
      },
      {
        header: "Amount",
        accessorKey: "quotation_total_amount",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: any) => {
          return (
            <span className="">
              ₹{" "}
              {Number(row.original.quotation_total_amount || 0).toLocaleString(
                "en-IN",
                { maximumFractionDigits: 2 }
              )}
            </span>
          );
        },
      },

      {
        header: "Follow Up Type",
        accessorKey: "follow_up_type",
        enableColumnFilter: true,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/api/quotation/column-filter-dropdown/?column_filter=follow_up_type",
        cell: ({ row }: { row: any }) => (
          <div onClick={(e) => e.stopPropagation()} style={{ width: "100%" }}>
            <FollowUpTypeCell
              row={row}
              handleFollowUpTypeChange={handleFollowUpTypeChange}
              canChange={false}
            />
          </div>
        ),
      },

      {
        header: "Follow Up Date",
        accessorKey: "follow_up_date",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const date = formatDate(row.original.follow_up_date);
          return <span>{date || "-"}</span>;
        },
        meta: {
          rawValue: (row: any) => row.follow_up_date,
        },
      },

      {
        header: "Follow Up Remark",
        accessorKey: "follow_up_remark",
        enableColumnFilter: false,
        enableSorting: false,
        minSize: 300,
        size: 300,
        cell: ({ row }: { row: any }) => {
          const remark = row.original.follow_up_remark || "-";
          const hasLongText =
            remark.length > 100 || remark.split("\n").length > 4;

          const truncatedContent = (
            <div
              style={{
                whiteSpace: "normal",
                wordWrap: "break-word",
                minWidth: "300px",
                maxWidth: "400px",
                display: "-webkit-box",
                WebkitLineClamp: 4,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: "1.5",
                maxHeight: "6em", // 4 lines * 1.5 line-height
              }}
            >
              {remark}
            </div>
          );

          if (hasLongText && remark !== "-") {
            return (
              <ScrollableTooltip
                id={`tooltip-${row.original.quotation_id}`}
                content={remark}
                placement="auto"
              >
                {truncatedContent}
              </ScrollableTooltip>
            );
          }

          return truncatedContent;
        },
      },
      {
        header: "Next Follow Up Date",
        accessorKey: "next_follow_up_date",
        enableColumnFilter: true,
        filterType: "date" as const,
        enableSorting: true,
        cell: ({ row }: { row: any }) => {
          const date = formatDate(row.original.next_follow_up_date);
          return <span>{date || "-"}</span>;
        },
        meta: {
          rawValue: (row: any) => row.next_follow_up_date,
        },
      },

      {
        header: "Quotation Mode",
        accessorKey: "quotation_creation_type",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        cell: ({ row }: any) =>
          formatLabel(row.original.quotation_creation_type) || "-",
        filterFetchUrl:
          "/api/quotation/column-filter-dropdown/?column_filter=quotation_creation_type",
      },
      {
        header: "Notes",
        accessorKey: "notes",
        enableColumnFilter: false,
        enableSorting: false,
        minSize: 300,
        size: 300,
        cell: ({ row }: { row: any }) => {
          const notes = row.original.notes || "-";
          const hasLongText =
            notes.length > 100 ||
            (notes !== "-" && notes.split("\n").length > 4);

          const cellContent = (
            <div style={{ minWidth: "300px", maxWidth: "400px" }}>
              <EditableTextCell
                value={row.original.notes}
                rowId={row.original.quotation_id}
                fieldName="notes"
                canChange={
                  quotation_permissions?.permissions?.Update &&
                  row.original.current_status !== "PI Generated"
                }
                placeholder="Enter notes..."
                multiline={true}
                rows={2}
                truncateLines={4}
                successMessage="Notes updated successfully!"
                errorMessage="Failed to update notes"
                onSuccess={fetchQuotationsAfterUpdate}
              />
            </div>
          );

          // Show tooltip for long text
          if (hasLongText && notes !== "-") {
            return (
              <ScrollableTooltip
                id={`tooltip-notes-${row.original.quotation_id}`}
                content={notes}
                placement="auto"
              >
                {cellContent}
              </ScrollableTooltip>
            );
          }

          return cellContent;
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        filterType: "date" as const,
        enableSorting: true,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_name ?? "N/A";
          const date = formatDate(row.original.created_at);
          return `  ${name}  -  ${date}  `;
        },
      },

      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        filterType: "date" as const,
        enableSorting: true,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_name ?? "N/A";
          const date = formatDate(row.original.updated_at);
          return `  ${name}  -  ${date}  `;
        },
      },
      ...DynamicAction,
    ],
    [
      DynamicAction,
      pageIndex,
      pageSize,
      handleFollowUpTypeChange,
      handleStatusChange,
      fetchQuotationsAfterUpdate,
      quotation_permissions?.permissions?.Update,
    ]
  );

  // Follow-up type options
  const followUpTypeOptions = ["Whatsapp", "Email", "Call"];

  return (
    <LoaderErrorWrapper loading={loading} error={error}>
      <TableContainer
        columns={columns}
        data={data}
        totalRecords={rowCount}
        pageIndex={pageIndex}
        pageSize={pageSize}
        // onPageChange={setPageIndex}
        onPageChange={handlePageChange} // for persisted pagination across pages
        onPageSizeChange={setPageSize}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        colValue={setColValue}
        sorting={sorting}
        onSortingChange={setSorting}
        isBordered={false}
        isPagination={true}
        isGlobalFilter={false}
        SearchPlaceholder="Search ..."
        manualSorting={true}
      />

      {/* Follow Up Details Modal */}
      <Modal
        show={showFollowUpModal}
        onHide={handleCloseFollowUpModal}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Follow Up Details
            {currentRowData?.quotation_auto_id && (
              <span className="ms-2 text-muted">
                - {currentRowData.quotation_auto_id}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Form Section with Accordion */}
          <div className="mb-4">
            {currentRowData?.current_status === "PI Generated" ? (
              <div className="alert alert-info">
                <i className="bi bi-info-circle me-2"></i>
                This quotation has been marked as PI Generated. New follow-ups
                cannot be added.
              </div>
            ) : (
              <Accordion
                activeKey={accordionOpen}
                onSelect={(e) => setAccordionOpen(e as string | null)}
              >
                <Accordion.Item eventKey="0">
                  <Accordion.Header>
                    <h6 className="mb-0">Add New Follow Up</h6>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Form>
                      <Row className="bg-light p-2 rounded">
                        {/* Follow Up Type */}
                        <Col xs={12} md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              Follow Up Type{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Autocomplete
                              disablePortal
                              size="small"
                              fullWidth
                              options={followUpTypeOptions}
                              value={followUpFormData.follow_up_type || null}
                              ListboxProps={
                                autocompleteDropdownStyles.ListboxProps
                              }
                              renderOption={(props, option) =>
                                autocompleteDropdownStyles.renderOption(
                                  props,
                                  option,
                                  (option) => option
                                )
                              }
                              onChange={(_, newValue) => {
                                setFollowUpFormData({
                                  ...followUpFormData,
                                  follow_up_type: newValue || "",
                                });
                              }}
                              noOptionsText="No types found"
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Select Type"
                                  variant="outlined"
                                  size="small"
                                  required
                                  sx={{
                                    ...autocompleteDropdownStyles.inputSx,
                                    backgroundColor: "#DDE1EF",
                                    "& .MuiInputBase-root": {
                                      backgroundColor: "#ffffffff",
                                    },
                                    "& .MuiFormHelperText-root": {
                                      margin: 0,
                                      padding: "4px 6px",
                                      lineHeight: 1.4,
                                    },
                                  }}
                                />
                              )}
                            />
                          </Form.Group>
                        </Col>

                        {/* Follow Up Date */}
                        <Col xs={12} md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              Follow Up Date{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="date"
                              value={followUpFormData.follow_up_date || ""}
                              onChange={(e) =>
                                setFollowUpFormData({
                                  ...followUpFormData,
                                  follow_up_date: e.target.value,
                                })
                              }
                              required
                            />
                          </Form.Group>
                        </Col>

                        {/* Next Follow Up Date */}
                        <Col xs={12} md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              Next Follow Up Date{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="date"
                              value={followUpFormData.next_follow_up_date || ""}
                              onChange={(e) =>
                                setFollowUpFormData({
                                  ...followUpFormData,
                                  next_follow_up_date: e.target.value,
                                })
                              }
                              min={followUpFormData.follow_up_date || undefined}
                              required
                            />
                          </Form.Group>
                        </Col>

                        {/* Follow Up Remark */}
                        <Col xs={12} md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              Follow Up Remark{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={followUpFormData.follow_up_remark || ""}
                              onChange={(e) =>
                                setFollowUpFormData({
                                  ...followUpFormData,
                                  follow_up_remark: e.target.value,
                                })
                              }
                              placeholder="Enter follow up remark..."
                              required
                            />
                          </Form.Group>
                        </Col>

                        <Col xs={12} className="text-center mt-2">
                          <Button
                            variant="primary"
                            onClick={handleUpdateFollowUp}
                            disabled={updatingFollowUp}
                          >
                            {updatingFollowUp ? (
                              <>
                                <Spinner size="sm" className="me-2" />
                                Adding...
                              </>
                            ) : (
                              "Add"
                            )}
                          </Button>
                        </Col>
                      </Row>
                    </Form>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            )}
          </div>

          {/* History Section */}
          <div className="mt-4">
            <h6 className="mb-3">Follow Up History</h6>
            {loadingHistory ? (
              <div className="text-center py-3 text-primary">
                <Spinner
                  animation="border"
                  size="sm"
                  className="text-primary"
                />
                <span className="ms-2">Loading history...</span>
              </div>
            ) : followUpHistory.length > 0 || currentRowData ? (
              <div
                style={{
                  maxHeight: "500px",
                  overflowY: "auto",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                }}
              >
                <Table striped bordered hover responsive>
                  <thead
                    style={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#fff",
                      zIndex: 10,
                    }}
                  >
                    <tr>
                      <th style={{ width: "50px" }}>Sr. No.</th>
                      <th style={{ minWidth: "100px" }}>Follow Up Type</th>
                      <th style={{ minWidth: "100px" }}>Follow Up Date</th>
                      <th>Remark</th>
                      <th style={{ minWidth: "100px" }}>Next Follow Up Date</th>
                      <th style={{ minWidth: "100px" }}>Created By</th>
                      <th style={{ minWidth: "100px" }}>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Calculate total count for descending order
                      const totalCount = 0 + followUpHistory.length;
                      let srNo = totalCount;

                      return (
                        <>
                          {/* Historical records - Continuing descending order */}
                          {followUpHistory.map(
                            (historyItem: any, index: number) => (
                              <tr key={historyItem.history_id || index}>
                                <td style={{ fontWeight: "bold" }}>{srNo--}</td>
                                <td>{historyItem.follow_up_type || "-"}</td>
                                <td>
                                  {historyItem.follow_up_date
                                    ? formatDate(historyItem.follow_up_date)
                                    : "-"}
                                </td>{" "}
                                <td>{historyItem.follow_up_remark || "-"}</td>
                                <td>
                                  {historyItem.next_follow_up_date
                                    ? formatDate(
                                        historyItem.next_follow_up_date
                                      )
                                    : "-"}
                                </td>
                                <td>{historyItem.changed_by || "-"}</td>
                                <td>
                                  {historyItem.changed_at
                                    ? formatDate(historyItem.changed_at)
                                    : "-"}
                                </td>
                              </tr>
                            )
                          )}
                        </>
                      );
                    })()}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-3 text-muted">
                No follow-up history available
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="secondary" onClick={handleCloseFollowUpModal}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </LoaderErrorWrapper>
  );
};

export default QuotationLog;
